import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Save, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmailSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [fromEmail, setFromEmail] = useState("presyejoel@gmail.com");
  const [apiKey, setApiKey] = useState("");
  const [maskedKey, setMaskedKey] = useState("");
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  async function fetchSettings() {
    try {
      const res = await fetch(API_URL + "/api/organization/email-settings", { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setConfigured(d.configured);
        setEnabled(d.enabled);
        setFromEmail(d.from_email || "presyejoel@gmail.com");
        setMaskedKey(d.api_key_masked || "");
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }

  async function handleSave() {
    if (!fromEmail.trim()) { toast.error("L'adresse d'expéditeur est requise"); return; }
    setSaving(true);
    try {
      const body = { from_email: fromEmail.trim(), enabled };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const res = await fetch(API_URL + "/api/organization/email-settings", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const d = await res.json();
        setConfigured(d.configured);
        setEnabled(d.enabled);
        setMaskedKey(d.api_key_masked || "");
        setApiKey("");
        toast.success("Configuration email enregistrée");
      } else {
        const err = await res.json();
        toast.error(typeof err.detail === "string" ? err.detail : "Erreur");
      }
    } catch (e) { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  }

  async function handleTest() {
    if (!testEmail.trim()) { toast.error("Entrez une adresse pour le test"); return; }
    setTesting(true);
    try {
      const res = await fetch(API_URL + "/api/organization/email-settings/test", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: testEmail.trim() })
      });
      const d = await res.json();
      if (res.ok) { toast.success(d.message || "Email de test envoyé"); }
      else { toast.error(typeof d.detail === "string" ? d.detail : "Échec de l'envoi"); }
    } catch (e) { toast.error("Erreur lors du test"); }
    finally { setTesting(false); }
  }

  return (
    <Card data-testid="email-settings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Configuration email (Resend)
        </CardTitle>
        <CardDescription>
          Connectez votre clé API Resend pour envoyer automatiquement les emails (bienvenue, invitations, rappels).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div className="space-y-0.5">
                <Label className="text-sm">Activer l'envoi d'emails</Label>
                <p className="text-xs text-muted-foreground">
                  {configured
                    ? "Une clé API est enregistrée."
                    : "Aucune clé enregistrée — les emails sont simulés."}
                </p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="email-enabled-switch" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="from-email">Adresse d'expéditeur</Label>
              <Input
                id="from-email"
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="noreply@votredomaine.com"
                data-testid="from-email-input"
              />
              <p className="text-xs text-amber-600">
                Le domaine de cette adresse doit être vérifié dans votre compte Resend, sinon l'envoi échouera.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-key">Clé API Resend</Label>
              <Input
                id="api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={configured ? "Clé enregistrée : " + maskedKey + " (laisser vide pour conserver)" : "re_..."}
                data-testid="api-key-input"
              />
              {configured && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Clé enregistrée ({maskedKey})
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} data-testid="save-email-settings-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-2">
              <Label htmlFor="test-email">Envoyer un email de test</Label>
              <div className="flex gap-2">
                <Input
                  id="test-email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="votre@email.com"
                  data-testid="test-email-input"
                />
                <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="send-test-email-btn">
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Tester
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
